<script lang="ts">
  import { ArrowLeft, ExternalLink } from "@lucide/svelte";
  import type { LegalPage } from "../model";

  let { page }: { page: LegalPage } = $props();
</script>

<svelte:head>
  <title>{page === "privacy" ? "Privacy Policy" : "Terms of Service"} | Meeting Radar</title>
</svelte:head>

<main class="legal-screen">
  <header class="legal-header">
    <a class="legal-back" href="/"><ArrowLeft size={17} aria-hidden="true" />Back to Meeting Radar</a>
    <a class="legal-company" href="https://metatheory.gg/" target="_blank" rel="noreferrer">Metatheory<ExternalLink size={14} aria-hidden="true" /></a>
  </header>

  <article class="legal-document">
    <div class="legal-title">
      <p>Meeting Radar</p>
      <h1>{page === "privacy" ? "Privacy Policy" : "Terms of Service"}</h1>
      <p>Last updated July 11, 2026</p>
    </div>

    {#if page === "privacy"}
      <p>This Privacy Policy explains how Metatheory, Inc. ("Metatheory," "we," or "us") handles information when your organization uses Meeting Radar.</p>

      <section>
        <h2>Information we handle</h2>
        <p>When you sign in with Google, Meeting Radar receives your name, email address, profile image, and verified hosted-domain information from Google. We use this information to authenticate you and determine whether your account belongs to an authorized organization.</p>
        <p>Meeting Radar stores authentication and session records, organization and membership routing data, and integration status. For each organization, it also stores the SoWork API credential supplied by an administrator, meeting and presence state, and webhook metadata needed to operate and troubleshoot the service.</p>
        <p>Display mode and color preferences are stored locally in your browser using localStorage.</p>
      </section>

      <section>
        <h2>How we use information</h2>
        <p>We use this information to provide sign-in, connect your organization to SoWork, show current meeting and presence activity, maintain sessions, secure and operate the service, and diagnose failures. We do not sell personal information or use it for advertising.</p>
      </section>

      <section>
        <h2>Service providers</h2>
        <p>Meeting Radar uses Cloudflare for application hosting, storage, and infrastructure; Google for authentication; and SoWork for the workspace activity your organization connects. These providers process information under their own terms and privacy practices.</p>
      </section>

      <section>
        <h2>Retention and user controls</h2>
        <p>We retain information as needed to operate Meeting Radar, protect the service, resolve issues, and meet applicable obligations. Retention can vary by data type and operational need.</p>
        <p>You can sign out of Meeting Radar, manage your Google account through Google, and clear local display preferences through your browser. Organization administrators control the SoWork credential provided to Meeting Radar. To ask about access, correction, or deletion of information associated with your account, contact us at <a href="mailto:support@metatheory.gg">support@metatheory.gg</a>.</p>
      </section>

      <section>
        <h2>Security and changes</h2>
        <p>We use technical and organizational measures intended to protect the service and the information it handles. No online service can guarantee absolute security.</p>
        <p>We may update this policy as Meeting Radar or its data practices change. We will post the revised policy here and update the date above.</p>
      </section>
    {:else}
      <p>These Terms of Service ("Terms") govern access to Meeting Radar, an online service provided by Metatheory, Inc. By accessing Meeting Radar, you agree to these Terms and represent that you are authorized to use it for your organization.</p>

      <section>
        <h2>Accounts and authorized use</h2>
        <p>Meeting Radar is for authorized business users with a supported company Google account. You are responsible for your account activity and for keeping access to your Google account secure. Organization administrators are responsible for having authority to connect their organization's SoWork account and provide its API credential.</p>
      </section>

      <section>
        <h2>Acceptable use</h2>
        <p>You may use Meeting Radar only for lawful, authorized purposes. You may not attempt to bypass access controls, disrupt or overload the service, probe it for vulnerabilities without permission, access another organization's data, or use the service in a way that violates applicable law or another person's rights.</p>
      </section>

      <section>
        <h2>Third-party services</h2>
        <p>Meeting Radar depends on services provided by Google, Cloudflare, and SoWork. Your use of those services may be governed by separate terms. We are not responsible for third-party services, and changes or interruptions to them may affect Meeting Radar.</p>
      </section>

      <section>
        <h2>Availability, changes, and termination</h2>
        <p>We may modify, suspend, or discontinue features or access to Meeting Radar. We may restrict or terminate access when reasonably necessary to protect the service, comply with law, or address a violation of these Terms. You may stop using the service at any time.</p>
      </section>

      <section>
        <h2>Disclaimers and liability</h2>
        <p>Meeting Radar is provided "as is" and "as available." To the extent permitted by law, Metatheory disclaims implied warranties, including merchantability, fitness for a particular purpose, and non-infringement. We do not guarantee uninterrupted, error-free, or completely secure operation.</p>
        <p>To the extent permitted by law, Metatheory will not be liable for indirect, incidental, special, consequential, or punitive damages, or for lost profits, revenues, data, or business opportunities arising from Meeting Radar.</p>
      </section>

      <section>
        <h2>Changes and contact</h2>
        <p>We may update these Terms. We will post revised Terms here and update the date above. Continued use after an update means you accept the revised Terms.</p>
        <p>Questions about these Terms may be sent to <a href="mailto:support@metatheory.gg">support@metatheory.gg</a>.</p>
      </section>
    {/if}

    <footer class="legal-footer">
      {#if page === "privacy"}<a href="/terms">Read the Terms of Service</a>{:else}<a href="/privacy">Read the Privacy Policy</a>{/if}
      <a href="/">Return to Meeting Radar</a>
    </footer>
  </article>
</main>
